import { isValidElement, type ReactNode } from "react";
import { type PaymentMethod } from "../../common/paymentConfig";

export type FAQItem = {
  id: string;
  question: string;
  answer: JSX.Element;
};

export function getFAQItems(
  onOpenPaymentModal: (method: PaymentMethod) => void,
): FAQItem[] {
  return [
    {
      id: "faq-storage",
      question: "How should I safely transport my desserts?",
      answer: (
        <>
          <p>
            Cakes and cupcakes are extremely delicate and should be handled with
            care. Cupcakes can tip over easily, so be sure to keep the box level
            at all times and avoid sudden movements. Once picked up, place them
            on the floorboard of your vehicle rather than on a seat, and avoid
            transporting them in the trunk as it tends to be a bumpier ride.
            Drive carefully, especially when making turns, and keep the A/C on
            during warmer weather.
          </p>
          <p>
            Cookies and cakesicles are more durable and generally don't require
            the same level of care during transport.
          </p>
        </>
      ),
    },
    {
      id: "faq-refrigeration",
      question: "Do my desserts need to be refrigerated?",
      answer: (
        <>
          <p>
            If your event isn't until later in the day, keep your cake
            refrigerated for stability. For the best flavor and texture, remove
            it from the fridge a few hours before serving so the buttercream can
            soften to room temperature.
          </p>
          <p>
            Cupcakes can generally be left at room temperature unless they
            contain perishable fillings. Refrigeration can quicken the staling
            process since they don't have the same protective layer of
            buttercream as cakes.
          </p>
          <p>
            Cookies do not need to be refrigerated and can be stored at room
            temperature.
          </p>
          <p>
            Cakesicles can also be stored at room temperature. Avoid leaving
            them in direct sunlight or the outer chocolate coating may melt.
          </p>
        </>
      ),
    },
    {
      id: "faq-shelf-life",
      question: "What's the shelf life of your desserts?",
      answer: (
        <>
          <p>
            Cakes are best enjoyed within 3 days, while cupcakes are best
            enjoyed within 1–2 days. Please note that certain flavors, such as
            vanilla, tend to dry out more quickly than others. It's best to
            seal-tight any leftovers to extend shelf life.
          </p>
          <p>
            Cookies stay fresh for up to 2 weeks, and cakesicles for about 1
            week when stored properly at room temperature. Both cookies and
            cakesicles can also be frozen for longer storage.
          </p>
        </>
      ),
    },
    {
      id: "faq-order-lead-time",
      question: "How far in advance should I place my order?",
      answer: (
        <>
          <p>
            A minimum of two weeks notice is required for most custom orders.
            Exceptions may be made for established corporate clients based on
            availability.
          </p>
          <p>
            For personal events with less than two weeks notice, orders are
            subject to availability and will incur a rush fee of 25% (minimum
            $50).
          </p>
        </>
      ),
    },
    {
      id: "faq-payment",
      question: "What payment options do you accept? Do you require a deposit?",
      answer: (
        <>
          <p>
            A $100 deposit is required to secure your order, with the remaining
            balance due upon pickup or delivery.
          </p>
          <p>
            Preferred methods of payment are through{" "}
            <button
              type="button"
              className="faq-inline-link"
              onClick={() => onOpenPaymentModal("zelle")}
            >
              Zelle (connie@conniescakerie.com)
            </button>{" "}
            or{" "}
            <button
              type="button"
              className="faq-inline-link"
              onClick={() => onOpenPaymentModal("venmo")}
            >
              Venmo (@conniescakerie)
            </button>
            . Payment via cash, check, or credit card are also accepted. Please
            note a 3.5% service fee will be applied to any card transactions.
          </p>
        </>
      ),
    },
    {
      id: "faq-samples",
      question: "Do you offer any cake samples?",
      answer: (
        <p>
          Since every cake is made to order, I don't keep sample cakes on hand
          for tastings. However, I do offer 4-count cupcake boxes for purchase
          ($20) if I am already baking a batch of a particular flavor. If
          there's a flavor you'd like to try, let me know and I'll reach out the
          next time it's available!
        </p>
      ),
    },
    {
      id: "faq-shipping",
      question: "Do you ship your products?",
      answer: (
        <p>
          Shipment within the U.S. is available only for cookies and cakesicles.
          Otherwise, other desserts are available for pickup in Mountain View or
          delivery within the Bay Area.
        </p>
      ),
    },
    {
      id: "faq-cake-height",
      question: "How tall are your cakes? How many layers do they have?",
      answer: (
        <p>
          My cakes are generally 4 layers tall (~4 inches in height), with an
          option to upgrade to a 6-layer cake if you prefer a taller look.
        </p>
      ),
    },
    {
      id: "faq-tiers-vs-layers",
      question: "What is the difference between tiers and layers?",
      answer: (
        <p>
          Tiers are separate cakes stacked on top of one another – for example,
          a two-tier cake consists of two stacked cakes. Layers are the
          individual cake layers inside each tier, separated by buttercream
          filling.
        </p>
      ),
    },
    {
      id: "faq-allergens",
      question: "Can you accommodate dietary restrictions and allergies?",
      answer: (
        <p>
          Many items can be customized for dietary preferences. Please check
          customizations in the specific item's description on the{" "}
          <a href="/menu">menu</a> for details.
        </p>
      ),
    },
  ];
}

function getNodeText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join("");
  }

  if (isValidElement(node)) {
    const children = (node.props as { children?: ReactNode }).children;
    const text = getNodeText(children);

    if (node.type === "p") {
      return `${text} `;
    }

    return text;
  }

  return "";
}

export function getFAQPageSchema() {
  const items = getFAQItems(() => undefined);

  return {
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: getNodeText(item.answer).replace(/\s+/g, " ").trim(),
      },
    })),
  };
}
